import React from 'react';

export const createClampBoardZoom =
  (min = 0.4, max = 2.5) =>
  value =>
    Math.min(max, Math.max(min, value));

export const useBoardCanvasViewport = props => {
  const {
    containerRef,
    isAnyModalOpen,
    onZoomChange,
    onViewportChange,
    zoomCommand,
    zoomCommandTick,
    initialViewport,
    clampZoom = createClampBoardZoom(),
  } = props;

  const normalizeViewport = React.useCallback(
    value => ({
      x: Number(value?.x) || 0,
      y: Number(value?.y) || 0,
      zoom: clampZoom(Number(value?.zoom) || 1),
    }),
    [clampZoom],
  );

  const [viewport, setViewport] = React.useState(() =>
    normalizeViewport(initialViewport),
  );
  const lastAppliedInitialViewportRef = React.useRef(
    normalizeViewport(initialViewport),
  );
  const lastHandledZoomCommandTickRef = React.useRef(null);

  const handleWheel = React.useCallback(
    event => {
      if (isAnyModalOpen) {
        return;
      }
      if (
        event?.target instanceof Element &&
        event.target.closest('[data-modal-root="true"]')
      ) {
        return;
      }

      const shouldAllowInnerScroll = target => {
        if (target instanceof Element) {
          const wheelLockedArea = target.closest(
            '.board-grid-no-drag, [data-board-wheel-lock="true"]',
          );
          if (wheelLockedArea) {
            return true;
          }
        }

        let node = target;
        while (node && node !== containerRef.current) {
          if (node instanceof HTMLElement) {
            const style = window.getComputedStyle(node);
            const overflowY = style.overflowY;
            const overflowX = style.overflowX;
            const canScrollY =
              (overflowY === 'auto' || overflowY === 'scroll') &&
              node.scrollHeight > node.clientHeight;
            const canScrollX =
              (overflowX === 'auto' || overflowX === 'scroll') &&
              node.scrollWidth > node.clientWidth;
            if (canScrollY || canScrollX) {
              return true;
            }
          }
          node = node.parentElement;
        }
        return false;
      };

      const isZoom = event.ctrlKey || event.metaKey;
      if (shouldAllowInnerScroll(event.target) && !isZoom) {
        return;
      }

      if (event?.cancelable) {
        event.preventDefault();
      }
      event.stopPropagation?.();
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) {
        return;
      }
      if (!isZoom) {
        setViewport(prev => ({
          ...prev,
          x: prev.x - event.deltaX,
          y: prev.y - event.deltaY,
        }));
        return;
      }

      const cursorX = event.clientX - rect.left;
      const cursorY = event.clientY - rect.top;
      setViewport(prev => {
        const nextZoom = clampZoom(
          prev.zoom * (event.deltaY > 0 ? 0.92 : 1.08),
        );
        const worldX = (cursorX - prev.x) / prev.zoom;
        const worldY = (cursorY - prev.y) / prev.zoom;
        return {
          x: cursorX - worldX * nextZoom,
          y: cursorY - worldY * nextZoom,
          zoom: nextZoom,
        };
      });
    },
    [clampZoom, containerRef, isAnyModalOpen],
  );

  React.useEffect(() => {
    const element = containerRef.current;
    if (!element) {
      return undefined;
    }
    element.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      element.removeEventListener('wheel', handleWheel);
    };
  }, [containerRef, handleWheel]);

  const onWheel = React.useCallback(() => {}, []);

  React.useEffect(() => {
    if (typeof onZoomChange === 'function') {
      onZoomChange(viewport.zoom);
    }
  }, [viewport.zoom, onZoomChange]);

  React.useEffect(() => {
    if (typeof onViewportChange === 'function') {
      onViewportChange(viewport);
    }
  }, [onViewportChange, viewport]);

  React.useEffect(() => {
    const nextInitialViewport = normalizeViewport(initialViewport);
    const previousInitialViewport = lastAppliedInitialViewportRef.current;
    const hasChanged =
      previousInitialViewport.x !== nextInitialViewport.x ||
      previousInitialViewport.y !== nextInitialViewport.y ||
      previousInitialViewport.zoom !== nextInitialViewport.zoom;
    if (!hasChanged) {
      return;
    }
    lastAppliedInitialViewportRef.current = nextInitialViewport;
    setViewport(nextInitialViewport);
  }, [initialViewport, normalizeViewport]);

  React.useEffect(() => {
    if (isAnyModalOpen) {
      return;
    }
    if (!zoomCommand || !Number.isFinite(zoomCommandTick)) {
      return;
    }
    if (lastHandledZoomCommandTickRef.current === zoomCommandTick) {
      return;
    }
    lastHandledZoomCommandTickRef.current = zoomCommandTick;
    setViewport(prev => {
      if (zoomCommand === 'reset') {
        return { ...prev, zoom: 1 };
      }
      if (zoomCommand === 'in') {
        return { ...prev, zoom: clampZoom(prev.zoom * 1.1) };
      }
      if (zoomCommand === 'out') {
        return { ...prev, zoom: clampZoom(prev.zoom / 1.1) };
      }
      return prev;
    });
  }, [zoomCommand, zoomCommandTick, isAnyModalOpen, clampZoom]);

  return {
    viewport,
    setViewport,
    onWheel,
    clampZoom,
  };
};
